/**
 * Entra (Azure AD) authentication setup using MSAL.js
 * Provides helpers for auth state and actions
 */

import { PublicClientApplication, type Configuration } from "@azure/msal-browser";
import type { AuthConfig, User } from "../types/index";
import { env } from "./env";

// Load configuration from environment variables
function loadAuthConfig(): AuthConfig {
  const requiredVars = {
    tenantId: env.REACT_APP_ENTRA_TENANT_ID,
    clientId: env.REACT_APP_ENTRA_CLIENT_ID,
    redirectUri: env.REACT_APP_ENTRA_REDIRECT_URI || window.location.origin,
  };

  const missing = Object.entries(requiredVars)
    .filter(([_, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    throw new Error(
      `Missing required Entra configuration: ${missing.join(", ")}. ` +
      `Check your .env file for REACT_APP_ENTRA_* variables.`
    );
  }

  return {
    tenantId: requiredVars.tenantId!,
    clientId: requiredVars.clientId!,
    redirectUri: requiredVars.redirectUri!,
  };
}

// Create MSAL configuration
function createMsalConfig(config: AuthConfig): Configuration {
  return {
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: config.redirectUri,
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      allowNativeBroker: false,
    },
  };
}

// Create and export MSAL instance
let msalInstance: PublicClientApplication | null = null;

export function getMsalInstance(): PublicClientApplication {
  if (!msalInstance) {
    const config = loadAuthConfig();
    const msalConfig = createMsalConfig(config);
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

// Check if user is authenticated
export function isAuthenticated(): boolean {
  const msal = getMsalInstance();
  const accounts = msal.getAllAccounts();
  return accounts.length > 0;
}

// Get current user from MSAL accounts
export function getUser(): User | null {
  const msal = getMsalInstance();
  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) return null;
  return {
    id: accounts[0].homeAccountId,
    name: accounts[0].name,
    email: accounts[0].username,
  };
}

// Get access token. Returns null when no user is signed in.
// Throws on token acquisition failure (e.g. InteractionRequiredAuthError) — callers must handle.
export async function getAccessToken(): Promise<string | null> {
  const msal = getMsalInstance();
  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) return null;

  const response = await msal.acquireTokenSilent({
    scopes: [], // No API scopes needed — site only reads public marketplace.json
    account: accounts[0],
  });
  return response.accessToken;
}

// Auth action: Login — full-page redirect (no popup)
export async function login(): Promise<void> {
  const msal = getMsalInstance();
  await msal.loginRedirect({ scopes: [] });
}

// Auth action: Logout
export async function logout(): Promise<void> {
  const msal = getMsalInstance();
  await msal.logoutRedirect();
}

// Initialize MSAL on app startup and handle redirect response
export async function initializeAuth(): Promise<void> {
  const msal = getMsalInstance();
  await msal.initialize();
  await msal.handleRedirectPromise();
}
